import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { WorkplaceClockInGuideButton } from "@src/appV2/redesign/Workplace/Profile/WorkplaceClockInGuideButton";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { getSentHomeRequestCreatePath } from "../../CreateSentHomeRequest/path";
import { useShiftOptionsActions } from "../hooks/useShiftActionVisibility";

interface ShiftOptionsBottomSheetProps {
  shift: WorkerShift;
  workplaceId: string;
  modalState: UseModalState;
  onClockIn: () => void;
  onUpdateETA: () => void;
  onCancelShift: () => void;
  onTransferShift: () => void;
  onUploadTimesheet?: () => void; // Optional for non-IP shifts
  onClose: () => void;
  currentDate: Date;
}

export const SHIFT_OPTIONS_LOG_EVENT_SOURCE_UI = "options-bottom-sheet";

export function ShiftOptionsBottomSheet(props: ShiftOptionsBottomSheetProps) {
  const {
    modalState,
    onClockIn,
    onUpdateETA,
    onCancelShift,
    onTransferShift,
    onUploadTimesheet,
    shift,
    workplaceId,
    onClose,
    currentDate,
  } = props;

  const history = useHistory();
  const actions = useShiftOptionsActions({ shift, currentDate });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <BottomSheet
      footer={<DialogFooter orientation="vertical" />}
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <Stack
        sx={{
          paddingTop: 4,
          paddingX: 4,
        }}
      >
        <Stack spacing={4}>
          {actions.clockIn.show && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CLOCK_IN_NOW_TAPPED, {
                  shiftId: shift._id,
                  sourceUI: SHIFT_OPTIONS_LOG_EVENT_SOURCE_UI,
                });
                onClockIn();
                onClose();
              }}
            >
              Clock in now
            </Button>
          )}

          {actions.showWorkplaceGuide && (
            <WorkplaceClockInGuideButton workplaceId={workplaceId} buttonSize="large" />
          )}

          {actions.updateEta.show && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LATE_FOR_SHIFT_TAPPED, {
                  shiftId: shift._id,
                  sourceUI: SHIFT_OPTIONS_LOG_EVENT_SOURCE_UI,
                });
                onUpdateETA();
              }}
            >
              Update ETA
            </Button>
          )}

          {actions.uploadTimesheet?.show && onUploadTimesheet && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => {
                onUploadTimesheet();
                onClose();
              }}
            >
              Upload timesheet
            </Button>
          )}

          {actions.sentHome.show && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SENT_HOME_TAPPED, {
                  shiftId: shift._id,
                });
                history.push(getSentHomeRequestCreatePath(shift._id));
              }}
            >
              I was sent home
            </Button>
          )}

          {actions.transferShift?.show && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_TRANSFER_SHIFT_TAPPED, {
                  shiftId: shift._id,
                  sourceUI: SHIFT_OPTIONS_LOG_EVENT_SOURCE_UI,
                });
                onTransferShift();
                onClose();
              }}
            >
              Transfer shift
            </Button>
          )}

          {actions.cancelShift.show && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              sx={{
                color: (theme) => theme.palette.error.light,
                borderColor: (theme) => theme.palette.border?.primary,
              }}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CANCEL_SHIFT_TAPPED, {
                  shiftId: shift._id,
                  sourceUI: SHIFT_OPTIONS_LOG_EVENT_SOURCE_UI,
                });
                onCancelShift();
                onClose();
              }}
            >
              Cancel shift
            </Button>
          )}
        </Stack>
      </Stack>
    </BottomSheet>
  );
}
